import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Row,
  Button,
  Avatar,
  Spin,
  message,
  Typography,
  Modal,
} from "antd";
import { EyeOutlined, DeleteOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import { CONTEST_MANAGEMENT_BASE, API_BASE_URL } from "../../services/apiConstant";

const { Title } = Typography;

interface MainObject {
  id: string;
  participantID: string;
  contestParticipantImageID: string;
  createdAt: string;
  position: number;
}

interface ParticipantImage {
  id: string;
  participantID: string;
  fileName: string;
  fileUrl: string;
}

interface Participant {
  id: string;
  fullName: string;
  emailAddress?: string;
  city?: string;
  countryName?: string;
  profileUrl?: string;
  participantImagesResponse: ParticipantImage[];
}

interface ContestDetails {
  id: string;
  title: string;
  description: string;
  contestParticipantViewResponse: Participant[];
}

interface MergedWinner extends MainObject {
  participant?: Participant;
  selectedImage?: ParticipantImage;
  fullName: string;
  city: string;
  countryName: string;
  imageUrl: string;
}

const WinnerList: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [mergedWinners, setMergedWinners] = useState<MergedWinner[]>([]);
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; winner?: MergedWinner }>({
    open: false,
    winner: undefined,
  });
  const [messageApi, contextHolder] = message.useMessage();

  const navigate = useNavigate();
  const location = useLocation();
  const place = (location.state as any)?.place;
  const contestId = (location.state as { contestId: string })?.contestId;

  const getStatusTitle = (place: number) => {
    switch (place) {
      case 1:
        return "1st Place Winner";
      case 2:
        return "2nd Place Winner";
      case 3:
        return "3rd Place Winner";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!contestId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) throw new Error("No token found");

        // Fetch contest details
        const contestEndpoint = `${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}/${contestId}`;
        const contestRes = await axios.get(contestEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const contest: ContestDetails = await decryptMessage(contestRes.data.Data, token);

        // Fetch winners
        const winnersEndpoint = `${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}/ContestWinner/${contestId}`;
        const winnersRes = await axios.get(winnersEndpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const mainObjects: MainObject[] = await decryptMessage(winnersRes.data.Data, token);

        // Merge winners with participant details + selected image
        const merged = mainObjects.map((main) => {
          const participant = contest.contestParticipantViewResponse.find(
            (p) => p.id === main.contestParticipantImageID
          );

          const selectedImage = participant?.participantImagesResponse.find(
            (img) => img.participantID === main.participantID
          );

          return {
            ...main,
            participant,
            selectedImage,
            fullName: participant?.fullName ?? "Unknown",
            city: participant?.city ?? "N/A",
            countryName: participant?.countryName ?? "N/A",
            imageUrl: selectedImage?.fileUrl || participant?.profileUrl || "",
          };
        });

        const filtered = merged.filter((w) => w.position === place);
        setMergedWinners(filtered);

      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to fetch winners");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contestId]);

  const handleRemove = async (winner: MergedWinner) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No token found");
      const payload = {
        id: winner.id,
        participantID: winner.participantID,
        contestParticipantImageID: winner.contestParticipantImageID,
        position: 0,
      };

      const res = await axios.put(
        `${API_BASE_URL + CONTEST_MANAGEMENT_BASE}/ContestWinner`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (res.status === 200) {
        messageApi.success("Winner removed successfully!");
        setMergedWinners((prev) => prev.filter((w) => w.id !== winner.id));
        setDeleteModal({ open: false, winner: undefined });
      } else {
        messageApi.error("Failed to update winner position");
      }
    } catch (err: any) {
      console.error("Error updating winner position:", err.response);
      messageApi.error("Error updating winner position");
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
        <img
          src="/images/newLoading.gif" // <-- path to your GIF
          alt="Loading..."
          style={{ width: 200, height: 150 }}
        />
      </div>
    );
  }

  const handleNavigateToDetails = (winner: any, contestId: string) => {
    navigate("/participantDetails", {
      state: {
        participantId: winner.contestParticipantImageID, // or winner.participant?.id if that's what you need
        contestId,
        fullname: winner.participant?.fullName || "Unknown",
      },
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      {contextHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Title level={3} style={{ color: "#41787C" }}>
          <Button
            type="link"
            icon={
              <ArrowLeftOutlined
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#000",
                  marginRight: 10,
                }}
              />
            }
            onClick={() => navigate(-1)}
            style={{ marginBottom: "10px", padding: 0, color: "#363636" }}
          />
          {getStatusTitle(place)}
        </Title>
      </Row>

      <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        {mergedWinners.length > 0 ? (
          mergedWinners.map((winner) => (
            <Card
              key={`${winner.id}-${winner.participantID}-${winner.position}`}
              style={{
                borderRadius: 10,
                display: "flex",
                alignItems: "center",
                backgroundColor: "#F9F9FA",
              }}
              bodyStyle={{
                padding: "10px 20px",
                display: "flex",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Avatar
                src={winner.imageUrl}
                size={64}
                shape="square"
                style={{ marginRight: 20 }}
              />
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, color: "#2c6b63" }}>{winner.fullName}</h3>
                <p style={{ margin: 0, color: "#555" }}>
                  {winner.city} | {winner.countryName}
                </p>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleNavigateToDetails(winner, contestId)}
                >
                  View
                </Button>
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => setDeleteModal({ open: true, winner })}
                >
                  Remove
                </Button>
                <Modal
                  title="Are you sure you want to remove this winner?"
                  open={deleteModal.open}
                  onOk={() =>
                    deleteModal.winner && handleRemove(deleteModal.winner)
                  }
                  onCancel={() =>
                    setDeleteModal({ open: false, winner: undefined })
                  }
                  okText="Yes"
                  cancelText="No"
                  centered
                />
              </div>
            </Card>
          ))
        ) : (
          <div style={{ textAlign: "center", padding: 50, color: "#888" }}>
            No winner data found
          </div>
        )}
      </div>
    </div>
  );
};

export default WinnerList;
